import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Send, UserPlus, UserMinus, Users, CheckCircle2, XCircle, Clock, CheckCheck } from "lucide-react";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";

const ROLE_LABEL = {
  admin: "DT",
  coach: "Coach",
  player: "Joueur",
};

export default function ConvocationsTab({ match, isCoach, onReload }) {
  const players = (match.players || []).filter((p) => !p.is_banned && !p.is_pending && !p.is_placeholder);
  const availByPid = useMemo(() => {
    const m = {};
    (match.availabilities || []).forEach((a) => (m[a.player_id] = a));
    return m;
  }, [match]);

  const convokedIds = new Set(
    (match.availabilities || []).filter((a) => a.is_convoked).map((a) => a.player_id)
  );

  // Draft: players currently selected (starts synced with convoked)
  const [draft, setDraft] = useState(convokedIds);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const toggle = (pid) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const dirty =
    draft.size !== convokedIds.size ||
    [...draft].some((id) => !convokedIds.has(id));

  const saveSelection = async () => {
    setSaving(true);
    try {
      await api.post(`/matches/${match.id}/convocations`, {
        player_ids: Array.from(draft),
      });
      toast.success(`${draft.size} joueur(s) convoqué(s)`);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    if (!window.confirm(`Envoyer les convocations à ${convokedIds.size} joueur(s) ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/matches/${match.id}/convocations/send`);
      toast.success(`Convocations envoyées à ${data.count} joueur(s)`);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const sortedPlayers = [...players].sort((a, b) => {
    const rank = { player: 0, coach: 1, admin: 2 };
    return (rank[a.role] ?? 3) - (rank[b.role] ?? 3);
  });

  const responsesSummary = useMemo(() => {
    let confirmed = 0, declined = 0, pending = 0;
    for (const pid of convokedIds) {
      const st = availByPid[pid]?.status;
      if (st === "available") confirmed += 1;
      else if (st === "unavailable") declined += 1;
      else pending += 1;
    }
    return { confirmed, declined, pending };
  }, [convokedIds, availByPid]);

  const sentAt = match.convocations_sent_at;

  return (
    <div className="space-y-6" data-testid="convocations-tab">
      {/* Status summary */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-[#0B0F17] border border-[#E5B85C]/20 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] font-mono-stat uppercase tracking-widest text-[#E5B85C] mb-1 flex items-center gap-2">
              <Users className="w-3 h-3" /> Convocation officielle
            </div>
            <div className="font-display font-black text-2xl uppercase">
              {convokedIds.size} joueur(s) convoqué(s)
            </div>
            {sentAt && (
              <div className="mt-1 text-[11px] font-mono-stat text-slate-500 flex items-center gap-1">
                <CheckCheck className="w-3 h-3 text-emerald-400" />
                Convocations envoyées le{" "}
                {new Date(sentAt).toLocaleString("fr-FR", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            )}
          </div>
          <div className="flex gap-3 flex-wrap">
            <MiniStat label="Confirmés" value={responsesSummary.confirmed} color="#22C55E" Icon={CheckCircle2} />
            <MiniStat label="Refusés" value={responsesSummary.declined} color="#EF4444" Icon={XCircle} />
            <MiniStat label="En attente" value={responsesSummary.pending} color="#F59E0B" Icon={Clock} />
          </div>
        </div>

        {isCoach && (
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              data-testid="save-convocations"
              onClick={saveSelection}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide text-xs disabled:opacity-50"
            >
              {saving ? "Enregistrement…" : dirty ? `Enregistrer la sélection (${draft.size})` : "Sélection à jour"}
            </button>
            <button
              data-testid="send-convocations"
              onClick={send}
              disabled={sending || convokedIds.size === 0 || dirty}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold uppercase tracking-wide text-xs disabled:opacity-50"
              title={dirty ? "Enregistrez d'abord la sélection" : ""}
            >
              <Send className="w-4 h-4" />
              {sending ? "Envoi…" : sentAt ? "Renvoyer les convocations" : "Envoyer les convocations"}
            </button>
          </div>
        )}
      </div>

      {/* Player picker (coach only) */}
      {isCoach && (
        <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <div className="font-display font-bold text-sm uppercase">
              Sélection · {players.length} membre(s) du club
            </div>
            <div className="text-[11px] font-mono-stat text-slate-500 mt-1">
              Cochez les joueurs à convoquer, puis enregistrez la sélection.
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {sortedPlayers.map((p) => {
              const selected = draft.has(p.id);
              const previouslyConvoked = convokedIds.has(p.id);
              const resp = availByPid[p.id]?.status;
              return (
                <button
                  type="button"
                  key={p.id}
                  data-testid={`convocation-pick-${p.id}`}
                  onClick={() => toggle(p.id)}
                  className={`w-full p-3 flex items-center gap-3 text-left hover:bg-slate-800/60 transition-colors ${
                    selected ? "bg-emerald-500/5" : ""
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                      selected
                        ? "bg-emerald-500 border-emerald-500"
                        : "border-slate-600"
                    }`}
                  >
                    {selected && <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />}
                  </div>
                  <Avatar player={p} size={36} className="ring-1 ring-white/10" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate flex items-center gap-2 flex-wrap">
                      {p.name}
                      {p.jersey_number != null && (
                        <span className="text-[10px] font-mono-stat text-slate-500">#{p.jersey_number}</span>
                      )}
                      {p.role !== "player" && (
                        <span className="text-[9px] font-mono-stat uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#E5B85C]/15 border border-[#E5B85C]/30 text-[#E5B85C]">
                          {ROLE_LABEL[p.role]}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{p.position || "—"}</div>
                  </div>
                  {previouslyConvoked && !selected && (
                    <span className="text-[10px] font-mono-stat uppercase tracking-widest text-rose-300">
                      À retirer
                    </span>
                  )}
                  {previouslyConvoked && resp && (
                    <span
                      className={`text-[10px] font-mono-stat uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap ${
                        resp === "available"
                          ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                          : resp === "unavailable"
                          ? "bg-rose-500/15 text-rose-300 border border-rose-500/40"
                          : "bg-amber-500/15 text-amber-300 border border-amber-500/40"
                      }`}
                    >
                      {resp === "available" ? "Confirmé" : resp === "unavailable" ? "Refusé" : "En attente"}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Convoked list (view for everyone) */}
      {!isCoach && (
        <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 font-display font-bold text-sm uppercase">
            Joueurs convoqués
          </div>
          <div className="divide-y divide-white/5">
            {[...convokedIds].length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                Pas encore de convocation publiée par le coach.
              </div>
            ) : (
              [...convokedIds].map((pid) => {
                const p = players.find((x) => x.id === pid);
                if (!p) return null;
                const resp = availByPid[pid]?.status;
                return (
                  <div key={pid} className="p-3 flex items-center gap-3">
                    <Avatar player={p} size={36} className="ring-1 ring-white/10" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{p.name}</div>
                      <div className="text-xs text-slate-500">{p.position || "—"}</div>
                    </div>
                    <span
                      className={`text-[10px] font-mono-stat uppercase tracking-widest px-2 py-1 rounded-full ${
                        resp === "available"
                          ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                          : resp === "unavailable"
                          ? "bg-rose-500/15 text-rose-300 border border-rose-500/40"
                          : "bg-amber-500/15 text-amber-300 border border-amber-500/40"
                      }`}
                    >
                      {resp === "available" ? "Confirmé" : resp === "unavailable" ? "Refusé" : "En attente"}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, color, Icon }) {
  return (
    <div
      className="px-3 py-2 rounded-lg border flex items-center gap-2"
      style={{ background: `${color}12`, borderColor: `${color}55` }}
    >
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <span className="font-mono-stat font-black text-lg" style={{ color }}>{value}</span>
      <span className="text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
    </div>
  );
}
